import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowLeft, QrCode } from "lucide-react";
import { toBlob } from "html-to-image";
import { API, LOGO_URL } from "../lib/api";

export default function JobsFlyer() {
  const [logoSrc, setLogoSrc] = useState(LOGO_URL);
  const [qrSrc, setQrSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);

  // Публичный URL формы вакансий (в проде — реальный домен)
  const jobsUrl = `${window.location.origin}/jobs`;
  const qrUrl = `${API}/qr/data?text=${encodeURIComponent(jobsUrl)}`;

  useEffect(() => {
    // логотип и QR инлайним как data-URL, чтобы экспорт html-to-image был чистым
    fetch(LOGO_URL).then((r) => r.blob()).then((b) => {
      const fr = new FileReader(); fr.onload = () => setLogoSrc(fr.result); fr.readAsDataURL(b);
    }).catch(() => {});
    fetch(qrUrl).then((r) => r.blob()).then((b) => {
      const fr = new FileReader(); fr.onload = () => setQrSrc(fr.result); fr.readAsDataURL(b);
    }).catch(() => setQrSrc(qrUrl));
  }, []);

  const downloadFlyer = async () => {
    const node = canvasRef.current;
    if (!node || busy) return;
    setBusy(true);
    try {
      try { await document.fonts.ready; } catch (e) { /* noop */ }
      const imgs = Array.from(node.querySelectorAll("img"));
      await Promise.all(imgs.map((img) => (img.complete && img.naturalWidth > 0)
        ? Promise.resolve()
        : new Promise((res) => { img.onload = res; img.onerror = res; setTimeout(res, 4000); })));
      await new Promise((r) => setTimeout(r, 100));
      const blob = await toBlob(node, { pixelRatio: 2, width: node.offsetWidth, height: node.offsetHeight, style: { margin: "0" } });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "CRAFT-FLYER-vakansii.png";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      alert("Не удалось создать изображение, попробуйте ещё раз");
    }
    setBusy(false);
  };

  const downloadQR = async () => {
    try {
      const r = await fetch(qrUrl);
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = "CRAFT-QR-vakansii.png";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      alert("Не удалось скачать QR");
    }
  };

  return (
    <div className="min-h-screen bg-[#2a2a2a] flex flex-col items-center py-8 px-4 gap-5">
      <div className="no-print flex items-center gap-3 w-full max-w-[720px] flex-wrap">
        <Link to="/admin/jobs" className="p-2.5 bg-white/10 rounded-xl text-white"><ArrowLeft size={16} /></Link>
        <div className="text-white font-display font-black flex-1">Флаер: Вакансии</div>
        <button onClick={downloadQR} className="admin-btn admin-btn-ghost"><QrCode size={15} /> Скачать QR</button>
        <button data-testid="jobsflyer-download-btn" onClick={downloadFlyer} disabled={busy} className="admin-btn"><Download size={15} /> {busy ? "Готовим..." : "Скачать флаер"}</button>
      </div>

      {/* A6 (105×148 мм) — красный постер вакансий */}
      <div ref={canvasRef} data-testid="jobsflyer-canvas"
        className="flyer-print w-[min(94vw,480px)] aspect-[105/148] relative overflow-hidden flex flex-col items-center shadow-2xl"
        style={{ background: "radial-gradient(120% 90% at 70% 20%, #FF2A2A 0%, #E10514 42%, #B4000E 100%)",
          // отступ под жёлтый подвал — от ширины самого флаера (проценты в padding считаются от родителя, на десктопе это ломало центровку QR)
          paddingBottom: "calc(min(94vw, 480px) * 0.145)" }}>
        {/* глянцевые блики */}
        <div className="absolute -right-10 top-6 w-40 h-40 rounded-full bg-white/10 blur-xl" />
        <div className="absolute right-2 top-1/3 w-24 h-40 rounded-full bg-orange-300/20 blur-2xl" />
        <div className="absolute -left-8 top-1/4 w-24 h-24 rounded-full bg-white/5 blur-lg" />

        {/* Шапка: логотип + бренд */}
        <div className="mt-[5%] flex items-center justify-center gap-[3%] px-[6%]">
          <img src={logoSrc} alt="" className="w-[15%] min-w-[52px] aspect-square rounded-2xl shadow-lg shrink-0" />
          <div className="font-display font-black uppercase text-black text-left tracking-tight leading-[0.82]">
            <div className="text-[clamp(28px,7vw,46px)]">CRAFT</div>
            <div className="text-[clamp(15px,3.6vw,24px)] tracking-[0.16em] leading-none">Fast Food</div>
          </div>
        </div>

        {/* Заголовок — «CRAFT jamoasiga qo'shiling» крупно, в стиле прежнего заголовка */}
        <div className="mt-[4%] text-center px-[7%]">
          <div className="font-display font-black uppercase text-white text-[clamp(17px,4.8vw,30px)] leading-[1.05] drop-shadow">CRAFT jamoasiga</div>
          <div className="font-display font-black uppercase text-white text-[clamp(17px,4.8vw,30px)] leading-[1.05] drop-shadow">{"qo'shiling"}</div>
        </div>

        {/* Призыв к сканированию QR — жёлтый акцент + чёрная плашка */}
        <div className="mt-[4%] w-[86%] text-center">
          <div className="font-display font-black text-[#FEBC10] text-[clamp(13px,3.4vw,21px)] leading-[1.15] drop-shadow">
            {"Bizning jamoamizga"}<br />{"qo'shilmoqchimisiz?"}
          </div>
          <div className="mt-[2.5%] bg-black rounded-full px-[6%] py-[1.6%] inline-flex items-center gap-2 whitespace-nowrap shadow-[0_10px_24px_-8px_rgba(0,0,0,0.6)]">
            <QrCode className="w-[clamp(14px,3vw,20px)] h-auto text-[#FEBC10] shrink-0" strokeWidth={2.5} />
            <span className="font-display font-black uppercase tracking-wide text-[#FEBC10] text-[clamp(11px,2.9vw,16px)] whitespace-nowrap">QR-kod orqali skaner qiling!</span>
          </div>
        </div>

        {/* QR — по центру между текстом сверху и жёлтым подвалом снизу */}
        <div className="flex-1 w-full flex items-center justify-center">
          <div className="bg-white rounded-[1.4rem] p-[3%] shadow-[0_18px_50px_-12px_rgba(0,0,0,0.5)]">
            {qrSrc ? (
              <img src={qrSrc} alt="QR" className="w-[clamp(118px,28vw,150px)] aspect-square block" crossOrigin="anonymous" />
            ) : (
              <div className="w-[clamp(118px,28vw,150px)] aspect-square" />
            )}
          </div>
        </div>

        {/* Жёлтый подвал */}
        <div className="absolute bottom-0 inset-x-0 bg-[#FEBC10] py-[3%] flex items-center justify-center gap-[2.5%]">
          <img src={logoSrc} alt="" className="w-[8%] min-w-[34px] aspect-square rounded-lg" />
          <span className="text-black font-display font-black text-[clamp(18px,4.6vw,30px)] tracking-wide">craft-oila.uz</span>
        </div>
      </div>
    </div>
  );
}
